import { useState, useEffect, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";

interface AuditEntry {
  id:            string;
  actor_id:      string | null;
  actor_type:    "user" | "worker" | "system";
  actor_email:   string | null;
  action:        string;
  resource_type: string | null;
  resource_id:   string | null;
  metadata:      Record<string, any>;
  occurred_at:   string;
}

const ACTION_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  "backup_job.completed":  { icon: "✓", label: "Backup completed",     color: "var(--teal)" },
  "backup_job.created":    { icon: "▶", label: "Backup started",       color: "var(--blue)" },
  "backup_job.failed":     { icon: "✕", label: "Backup failed",        color: "var(--red)" },
  "connector.authorised":  { icon: "⚡", label: "Connector authorised", color: "#8B5CF6" },
  "schedule.upserted":     { icon: "◷", label: "Schedule updated",     color: "#F59E0B" },
  "auth.login":            { icon: "→", label: "Login",                color: "var(--text3)" },
};

const FILTER_OPTIONS = [
  { value: "",                      label: "All activity" },
  { value: "backup_job.completed",  label: "Backups completed" },
  { value: "backup_job.failed",     label: "Backups failed" },
  { value: "backup_job.created",    label: "Backups created" },
  { value: "connector.authorised",  label: "Connector changes" },
  { value: "schedule.upserted",     label: "Schedule changes" },
  { value: "auth.login",            label: "Logins" },
];

function formatTime(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs  = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1)   return "just now";
  if (diffMins < 60)  return `${diffMins}m ago`;
  if (diffHrs  < 24)  return `${diffHrs}h ago`;
  if (diffDays < 7)   return `${diffDays}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatFullTime(ts: string): string {
  return new Date(ts).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function formatBytes(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function MetadataSummary({ action, metadata }: { action: string; metadata: Record<string, any> }) {
  if (action === "backup_job.completed") {
    const total = metadata.total_records
      ? Object.values(metadata.total_records as Record<string, number>).reduce((a, b) => a + b, 0)
      : 0;
    const size = formatBytes(metadata.total_bytes);
    return (
      <span style={{ color: "var(--text3)", fontSize: 12 }}>
        {total.toLocaleString()} records · {size} · {metadata.files} files
      </span>
    );
  }
  if (action === "backup_job.failed" && metadata.error) {
    return <span style={{ color: "var(--red)", fontSize: 12 }}>{String(metadata.error).slice(0, 80)}</span>;
  }
  if (action === "schedule.upserted") {
    return (
      <span style={{ color: "var(--text3)", fontSize: 12 }}>
        {metadata.cron} · {metadata.retention_days}d retention
      </span>
    );
  }
  if (action === "connector.authorised" && metadata.connector_type) {
    return <span style={{ color: "var(--text3)", fontSize: 12 }}>{metadata.connector_type}</span>;
  }
  return null;
}

function ExpandedMetadata({ metadata }: { metadata: Record<string, any> }) {
  if (!metadata || Object.keys(metadata).length === 0) return null;
  return (
    <div style={{
      marginTop: 10, padding: "10px 12px",
      background: "var(--surface2)", borderRadius: "var(--r)",
      border: "1px solid var(--border)",
      fontFamily: "var(--mono)", fontSize: 11,
      color: "var(--text2)", lineHeight: 1.7,
    }}>
      {Object.entries(metadata).map(([k, v]) => (
        <div key={k}>
          <span style={{ color: "var(--text3)" }}>{k}: </span>
          <span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AuditPage() {
  const { get } = useApi();
  const [entries, setEntries]       = useState<AuditEntry[]>([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState("");
  const [offset, setOffset]         = useState(0);
  const [expanded, setExpanded]     = useState<string | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const limit = 25;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (filter) params.set("action", filter);
    get<{ entries: AuditEntry[]; total: number }>(`/api/audit?${params}`)
      .then(r => { setEntries(r.entries ?? []); setTotal(r.total ?? 0); })
      .catch(err => setError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get, filter, offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOffset(0); }, [filter]);

  const grouped = entries.reduce<Record<string, AuditEntry[]>>((acc, entry) => {
    const date = new Date(entry.occurred_at).toLocaleDateString("en-GB", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric"
    });
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar
          title="Audit Log"
          subtitle={total ? `${total} events` : `${entries.length} events`}
          action={
            <select
              className="form-input"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ width: 180, padding: "6px 10px", fontSize: 13 }}
            >
              {FILTER_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          }
        />
        <main className="page">
          {error && <PageError message={error} onRetry={load} />}

          {!error && (loading && entries.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>Loading…</div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>⬡</div>
              <p style={{ fontWeight: 500, color: "var(--text2)" }}>No audit events</p>
            </div>
          ) : (
            <>
              {Object.entries(grouped).map(([date, dayEntries]) => (
                <div key={date} style={{ marginBottom: 24 }}>
                  {/* Date header */}
                  <div style={{
                    fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)",
                    letterSpacing: "0.07em", textTransform: "uppercase",
                    padding: "0 0 10px 0", borderBottom: "1px solid var(--border)",
                    marginBottom: 2,
                  }}>{date}</div>

                  {/* Entries for this day */}
                  {dayEntries.map(entry => {
                    const cfg = ACTION_CONFIG[entry.action] ?? { icon: "•", label: entry.action, color: "var(--text3)" };
                    const isExpanded = expanded === entry.id;

                    return (
                      <div
                        key={entry.id}
                        onClick={() => setExpanded(isExpanded ? null : entry.id)}
                        style={{
                          display: "flex", gap: 12, padding: "10px 0",
                          borderBottom: "1px solid var(--border)",
                          cursor: "pointer",
                        }}
                      >
                        {/* Icon */}
                        <div style={{
                          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                          background: "var(--surface2)", border: "1px solid var(--border)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, color: cfg.color, fontWeight: 600, marginTop: 2,
                        }}>
                          {cfg.icon}
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 13, fontWeight: 500, color: cfg.color }}>{cfg.label}</span>
                              {entry.actor_type === "user" && (entry.actor_email || entry.metadata?.actor_email || entry.actor_id) ? (
                                <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: "var(--r)", padding: "1px 6px" }}>
                                  {entry.actor_email || entry.metadata?.actor_email || entry.actor_id?.slice(0, 8) + "…"}
                                </span>
                              ) : entry.actor_type === "worker" ? (
                                <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)" }}>system</span>
                              ) : null}
                            </div>
                            <span
                              title={formatFullTime(entry.occurred_at)}
                              style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", flexShrink: 0 }}
                            >
                              {formatTime(entry.occurred_at)}
                            </span>
                          </div>

                          <MetadataSummary action={entry.action} metadata={entry.metadata} />

                          {/* Resource ID */}
                          {entry.resource_id && (
                            <div style={{ fontSize: 10, fontFamily: "var(--mono)", color: "var(--text3)", marginTop: 2 }}>
                              {entry.resource_type}/{entry.resource_id.slice(0, 8)}…
                            </div>
                          )}

                          {/* Expanded metadata */}
                          {isExpanded && <ExpandedMetadata metadata={entry.metadata} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Pagination */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, fontSize: 13, color: "var(--text3)" }}>
                <span>{offset + 1}–{Math.min(offset + limit, offset + entries.length)} of {total || "many"}</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-ghost btn-sm" disabled={offset === 0} onClick={() => setOffset(o => Math.max(0, o - limit))}>← Prev</button>
                  <button className="btn btn-ghost btn-sm" disabled={entries.length < limit} onClick={() => setOffset(o => o + limit)}>Next →</button>
                </div>
              </div>
            </>
          ))}
        </main>
      </div>
    </div>
  );
}